import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';
import SchedulePicker from './SchedulePicker';
import VideoRequestForm from './VideoRequestForm';
import { buildSchedule, todayLocal } from '../utils/schedule';

const STEPS = ['Student', 'Video', 'Prepare', 'Schedule'];
const SAVE_KEY = 'classcheck_video_wizard';

function loadSaved() {
  try { return JSON.parse(localStorage.getItem(SAVE_KEY)) || {}; } catch { return {}; }
}
function save(state) {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(state)); } catch { /* private window: no resume, still works */ }
}

const videoName = (v) => v.title || v.url;
const STATUS = { requested: 'Waiting for the worker', preparing: 'Preparing', needs_review: 'Ready to review', ready: 'Ready', failed: 'Failed' };

function progressText(v) {
  const p = v.progress;
  if (!p) return null;
  const pct = Math.round((p.progress || 0) * 100);
  return `${p.stage || 'working'}${p.progress != null ? ` (${pct}%)` : ''}`;
}

// A guided path through the three jobs: pick or add a student, pick or add a video, schedule its parts.
export default function VideoWizard({ onClose, onReview, onChanged }) {
  const saved = useRef(loadSaved()).current;
  const [step, setStep] = useState(0);
  const [students, setStudents] = useState(null);
  const [videos, setVideos] = useState(null);
  const [studentId, setStudentId] = useState(saved.studentId || null);   // number, or 'new'
  const [newStudent, setNewStudent] = useState({ name: '', email: '' });
  const [videoId, setVideoId] = useState(saved.videoId || null);          // number, or 'new'
  const [sched, setSched] = useState({ from: 1, to: 1, perDay: 1, date: todayLocal(), days: [1, 2, 3, 4, 5] });
  const [alreadyWatched, setAlreadyWatched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);   // { count, student, video }
  const heading = useRef(null);

  const loadVideos = useCallback(async () => {
    try { setVideos(await api.getVideos()); } catch (err) { toast.error(err.message); }
  }, []);

  useEffect(() => {
    api.getUsers().then(us => setStudents(us.filter(u => u.role === 'student'))).catch(err => toast.error(err.message));
    loadVideos();
  }, [loadVideos]);

  useEffect(() => { if (heading.current) heading.current.focus(); }, [step, done]);

  const student = students && students.find(s => s.id === studentId);
  const video = videos && typeof videoId === 'number' ? videos.find(v => v.id === videoId) : null;
  const busyVideo = video && (video.status === 'requested' || video.status === 'preparing');

  // keep the status fresh while the video is being prepared
  useEffect(() => {
    if (step !== 2 || !busyVideo) return undefined;
    const t = setInterval(loadVideos, 4000);
    return () => clearInterval(t);
  }, [step, busyVideo, loadVideos]);

  useEffect(() => {
    if (typeof studentId === 'number' || typeof videoId === 'number') save({ studentId: typeof studentId === 'number' ? studentId : null, videoId: typeof videoId === 'number' ? videoId : null });
  }, [studentId, videoId]);

  // default the part range to the whole video once we know how many parts it has
  const partCount = video ? video.parts : 0;
  useEffect(() => {
    if (partCount > 0) setSched(s => ({ ...s, from: 1, to: partCount }));
  }, [partCount, videoId]);

  const schedule = buildSchedule({ from: sched.from, to: sched.to, perDay: sched.perDay, days: sched.days, startDate: sched.date });

  async function nextFromStudent() {
    if (studentId === 'new') {
      setBusy(true);
      try {
        const u = await api.createUser({ name: newStudent.name.trim(), email: newStudent.email.trim() });
        setStudents(list => [...(list || []), u]);
        setStudentId(u.id);
        toast.success(`${u.name} added. They sign in with ${u.email}.`);
        setStep(1);
      } catch (err) { toast.error(err.message); }
      setBusy(false);
      return;
    }
    setStep(1);
  }

  async function markReady() {
    setBusy(true);
    try { await api.markVideoReady(videoId); await loadVideos(); toast.success('Marked ready'); }
    catch (err) { toast.error(err.message); }
    setBusy(false);
  }

  async function retry() {
    setBusy(true);
    try { await api.retryVideo(videoId); await loadVideos(); }
    catch (err) { toast.error(err.message); }
    setBusy(false);
  }

  async function finish() {
    setBusy(true);
    try {
      const r = await api.assignVideo({ video_id: videoId, student_ids: [studentId], schedule });
      if (alreadyWatched) for (const id of r.created) await api.waiveAllVideoWatch(id);
      setDone({ count: r.created.length, skipped: r.skipped, student: student.name, video: videoName(video) });
      save({ videoId });   // keep the video for "set up another student", forget the finished student
      onChanged();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  }

  function again() {
    setDone(null); setStudentId(null); setNewStudent({ name: '', email: '' }); setAlreadyWatched(false); setStep(0);
  }

  function reviewFirst() {
    save({ studentId, videoId });
    onReview(videoId);
  }

  // ---- what can the Next button do on this step? ----
  let canNext = false;
  if (step === 0) canNext = typeof studentId === 'number' || (studentId === 'new' && newStudent.name.trim() && /\S+@\S+\.\S+/.test(newStudent.email));
  if (step === 1) canNext = typeof videoId === 'number' && !!video && video.status !== 'failed';
  if (step === 2) canNext = !!video && video.status === 'ready';

  function next() {
    if (step === 0) nextFromStudent();
    else if (step === 3) finish();
    else setStep(step + 1);
  }

  if (done) {
    return (
      <div className="video-page">
        <section className="video-card wizard" aria-labelledby="wiz-h">
          <h2 id="wiz-h" tabIndex={-1} ref={heading}>All set</h2>
          <p>
            {done.count > 0
              ? <><strong>{done.student}</strong> now has {done.count} assignment{done.count === 1 ? '' : 's'} for “{done.video}”.</>
              : <>Those assignments already existed for <strong>{done.student}</strong>, so nothing new was added.</>}
            {alreadyWatched && done.count > 0 && ' The watch requirement is waived, so they can go straight to the quizzes.'}
          </p>
          <p className="video-meta">They sign in at this site and find the videos under “Today’s Videos” on the day each is due.</p>
          <div className="wizard-nav">
            <button type="button" className="btn-secondary" onClick={again}>Set up another student</button>
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="video-page">
      <section className="video-card wizard" aria-labelledby="wiz-h">
        <div className="wizard-top">
          <ol className="wizard-steps" aria-label="Setup steps">
            {STEPS.map((name, i) => (
              <li key={name} className={i === step ? 'current' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
                <span className="wizard-dot">{i < step ? '✓' : i + 1}</span><span className="wizard-step-name">{name}</span>
              </li>
            ))}
          </ol>
          <button type="button" className="btn-link wizard-close" onClick={onClose}>Close</button>
        </div>
        <p className="video-meta" aria-live="polite">Step {step + 1} of {STEPS.length}</p>

        {step === 0 && (
          <div>
            <h2 id="wiz-h" tabIndex={-1} ref={heading}>Who is this for?</h2>
            {students === null && <p className="video-meta">Loading students…</p>}
            <fieldset className="wizard-choices">
              <legend className="sr-only">Student</legend>
              {students && students.map(s => (
                <label key={s.id} className="wizard-choice">
                  <input type="radio" name="wiz-student" checked={studentId === s.id} onChange={() => setStudentId(s.id)} />
                  <span>{s.name} <span className="video-meta">{s.email}</span></span>
                </label>
              ))}
              <label className="wizard-choice">
                <input type="radio" name="wiz-student" checked={studentId === 'new'} onChange={() => setStudentId('new')} />
                <span>Add a new student</span>
              </label>
            </fieldset>
            {studentId === 'new' && (
              <div className="video-form wizard-fields">
                <label htmlFor="wiz-name">Name</label>
                <input id="wiz-name" type="text" autoComplete="off" value={newStudent.name}
                       onChange={e => setNewStudent(n => ({ ...n, name: e.target.value }))} />
                <label htmlFor="wiz-email">Email they will sign in with</label>
                <input id="wiz-email" type="email" autoComplete="off" inputMode="email" value={newStudent.email}
                       onChange={e => setNewStudent(n => ({ ...n, email: e.target.value }))} />
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 id="wiz-h" tabIndex={-1} ref={heading}>Which video?</h2>
            {videos === null && <p className="video-meta">Loading videos…</p>}
            <fieldset className="wizard-choices">
              <legend className="sr-only">Video</legend>
              {videos && videos.filter(v => v.status !== 'failed').map(v => (
                <label key={v.id} className="wizard-choice">
                  <input type="radio" name="wiz-video" checked={videoId === v.id} onChange={() => setVideoId(v.id)} />
                  <span>{videoName(v)} <span className={`status-chip ${v.status}`}>{STATUS[v.status]}</span>
                    {v.parts > 0 && <span className="video-meta"> {v.parts} parts</span>}</span>
                </label>
              ))}
              <label className="wizard-choice">
                <input type="radio" name="wiz-video" checked={videoId === 'new'} onChange={() => setVideoId('new')} />
                <span>Add a new video</span>
              </label>
            </fieldset>
            {videoId === 'new' && (
              <div className="wizard-fields">
                <VideoRequestForm embedded submitLabel="Prepare this video" onRequested={async (res) => {
                  await loadVideos();
                  setVideoId(res.id); setStep(2);
                }} />
                <p className="video-meta">“Number of parts” is how many quizzes the video is cut into. For about 45 minutes each, divide the
                  video’s length by 45 minutes (a 21-hour video is about 28).</p>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 id="wiz-h" tabIndex={-1} ref={heading}>Getting the quizzes ready</h2>
            {!video && <p className="video-meta">Loading…</p>}
            {video && <p><strong>{videoName(video)}</strong> <span className={`status-chip ${video.status}`}>{STATUS[video.status]}</span></p>}
            {busyVideo && (
              <div role="status" aria-live="polite">
                <p>{progressText(video) || 'The worker is picking this up.'}</p>
                <p className="video-meta">This can take a few minutes, or hours for a very long video. You can close this and come back: the wizard remembers your student and video.</p>
              </div>
            )}
            {video && video.status === 'failed' && (
              <div>
                <p className="video-error">{video.error || 'Preparing this video failed.'}</p>
                <button type="button" className="btn-secondary" onClick={retry} disabled={busy}>Try again</button>
              </div>
            )}
            {video && video.status === 'needs_review' && (
              <div>
                <p>{video.questions_included} questions are written across {video.parts} parts. Please skim them before the kids see them.</p>
                <div className="wizard-nav">
                  <button type="button" className="btn-primary" onClick={reviewFirst}>Review the questions</button>
                  <button type="button" className="btn-secondary" onClick={markReady} disabled={busy}>Mark ready without reviewing</button>
                </div>
                <p className="video-meta">After reviewing, tap “Mark ready to assign”, then open Guided setup again to carry on.</p>
              </div>
            )}
            {video && video.status === 'ready' && <p>The quizzes are ready.</p>}
          </div>
        )}

        {step === 3 && video && student && (
          <div className="video-form">
            <h2 id="wiz-h" tabIndex={-1} ref={heading}>When should {student.name} do it?</h2>
            <p className="video-meta">“{videoName(video)}” has {video.parts} parts.</p>
            <SchedulePicker idPrefix="wiz" parts={video.parts} value={sched} onChange={patch => setSched(v => ({ ...v, ...patch }))} />
            <label className="wizard-choice" style={{ marginTop: 12 }}>
              <input type="checkbox" checked={alreadyWatched} onChange={e => setAlreadyWatched(e.target.checked)} />
              <span>They already watched these parts. Let them go straight to the quizzes.</span>
            </label>
          </div>
        )}

        <div className="wizard-nav wizard-sticky">
          {step > 0 && <button type="button" className="btn-secondary" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>}
          {!(step === 1 && videoId === 'new') && (
            <button type="button" className="btn-primary" onClick={next}
                    disabled={busy || (step === 3 ? schedule.length === 0 : !canNext)}>
              {step === 3 ? (busy ? 'Scheduling…' : 'Schedule it') : busy ? 'Working…' : 'Next'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
